/* Copyright 2025 New Vector Ltd.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
 * Please see LICENSE files in the repository root for full details.
 */

// This script includes some optional javascript used in the server-side
// generated templates which enhances the user experience if loaded.
//
// Ideally later on we could find a way to hydrate full React components instead
// of doing this, as this can very quickly get out of hands.

for (const trigger of document.querySelectorAll<HTMLButtonElement>(
  ".password-guidance-trigger",
)) {
  const row = trigger.parentElement;
  if (!row) continue;
  const reset = () => {
    delete row.dataset.guidanceDismissed;
  };
  trigger.addEventListener("focus", reset);
  trigger.addEventListener("pointerenter", reset);
  row.addEventListener("pointerleave", reset);
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") row.dataset.guidanceDismissed = "";
  });
}

const registrationPassword = document.querySelector<HTMLInputElement>(
  "[data-registration-password]",
);
if (registrationPassword) {
  void Promise.all([
    import("../i18n"),
    import("../utils/registration-password"),
  ])
    .then(
      async ([
        { default: i18n, setupI18n },
        { enhanceRegistrationPassword },
      ]) => {
        await setupI18n();
        enhanceRegistrationPassword(registrationPassword, i18n.t.bind(i18n));
      },
    )
    .catch(() => {
      // Progressive enhancement: the server still validates on submission.
    });
}

const VALID_USERNAME_RE = /^\s*[a-z0-9]+(?:-[a-z0-9]+)*\s*$/;
const VALID_POSTNUMBER_BODY_RE = /^\s*[0-9]+\s*$/;
const VALID_ALPHANUMERIC_POSTNUMBER_BODY_RE =
  /^\s*[a-z0-9]+(?:-[a-z0-9]+)*\s*$/;

function extractPostnumberBodyValue(inputElement: HTMLInputElement) {
  const normalizedValue = inputElement.value.trim().toLocaleLowerCase();

  if (!normalizedValue) return "";
  return normalizedValue;
}

/** Grab the nearest error message inserted by the templates by error kind and code */
function grabErrorMessage(
  parentElement: HTMLElement | null,
  kind: string,
  code: string,
): HTMLElement | null {
  if (!parentElement) return null;
  const matching = parentElement.querySelectorAll<HTMLElement>(
    `[data-error-kind=${kind}][data-error-code=${code}]`,
  );
  // We potentially have duplicate error messages coming from the templates, one
  // hidden and one visible
  let el: HTMLElement | null = null;
  for (const element of matching) {
    // In case we're finding a non-hidden element, we prioritise that one
    if (!element.classList.contains("hidden")) return element;
    // Else it will be the last element in the list
    el = element;
  }
  return el;
}

/**
 * This patches a username input element to lowercase on input and trim on blur
 *
 * @param inputElement The input element to patch
 */
function patchUsernameInput(inputElement: HTMLInputElement) {
  const labelElement = inputElement.parentElement?.querySelector("label");
  // This is the list of elements which needs to have the data-invalid attribute
  // set/unset
  const fieldElements: HTMLElement[] = [inputElement];
  if (labelElement) fieldElements.push(labelElement);

  // Grab the translated 'invalid username' message from the DOM
  // TODO: we could expand this to other validation messages, but this is the
  // most important one for now
  const invalidUsernameMessage = grabErrorMessage(
    inputElement.parentElement,
    "policy",
    "username-invalid-chars",
  );
  if (!invalidUsernameMessage) {
    console.warn(
      "Could not find the error message in the DOM for username validation",
      inputElement,
    );
  }

  const clearInvalidState = () => {
    for (const el of fieldElements) el.removeAttribute("data-invalid");
    invalidUsernameMessage?.classList.add("hidden");
  };

  const showInvalidState = () => {
    for (const el of fieldElements) el.setAttribute("data-invalid", "");
    invalidUsernameMessage?.classList.remove("hidden");
  };

  const syncValidation = () => {
    if (inputElement.disabled) {
      inputElement.setCustomValidity("");
      clearInvalidState();
      return;
    }

    const bodyValue = extractPostnumberBodyValue(inputElement);
    const isMissingRequiredValue = inputElement.required && !bodyValue;
    const validBodyRe = inputElement.hasAttribute("data-country-prefixed-input")
      ? inputElement.dataset.postnumberValidationMode === "alphanumeric"
        ? VALID_ALPHANUMERIC_POSTNUMBER_BODY_RE
        : VALID_POSTNUMBER_BODY_RE
      : VALID_USERNAME_RE;
    const hasInvalidChars = Boolean(bodyValue) && !validBodyRe.test(bodyValue);

    inputElement.setCustomValidity(
      isMissingRequiredValue ? "Please fill out this field." : "",
    );

    if (hasInvalidChars) {
      showInvalidState();
    } else {
      clearInvalidState();
    }
  };

  inputElement.addEventListener("input", () => {
    // Simply lowercase things automatically, as this is not too disruptive
    inputElement.value = inputElement.value.toLocaleLowerCase();
    syncValidation();
  });

  // Sneakily trim the input on blur
  inputElement.addEventListener("blur", () => {
    inputElement.value = inputElement.value.trim();
    syncValidation();
  });

  inputElement.addEventListener("country-prefix-sync", syncValidation);
  syncValidation();
}

function patchCountryAwareForm(formElement: HTMLFormElement) {
  const countrySelect = formElement.querySelector<HTMLSelectElement>(
    "select[data-country-code-select]",
  );
  const usernameInput = formElement.querySelector<HTMLInputElement>(
    "input[data-choose-username]",
  );
  const countryPreviewInput = formElement.querySelector<HTMLInputElement>(
    "input[data-country-code-preview]",
  );
  const previewElements = formElement.querySelectorAll<HTMLElement>(
    "[data-localpart-preview]",
  );
  const previewInputs = formElement.querySelectorAll<HTMLInputElement>(
    "input[data-localpart-preview-input]",
  );
  const generateButton = formElement.querySelector<HTMLButtonElement>(
    "button[data-generate-postnumber]",
  );

  if (
    !countrySelect ||
    (!usernameInput &&
      previewElements.length === 0 &&
      previewInputs.length === 0)
  ) {
    return;
  }

  const hasCountryPrefixedInput =
    usernameInput?.hasAttribute("data-country-prefixed-input") ?? false;
  let currentCountry = countrySelect.value.trim().toLocaleLowerCase();
  const generateButtonLabel = generateButton?.getAttribute("aria-label") ?? "";

  const syncUsernameInput = () => {
    if (!usernameInput || !hasCountryPrefixedInput) {
      return usernameInput?.value.trim().toLocaleLowerCase() || "";
    }

    const body = extractPostnumberBodyValue(usernameInput);

    if (currentCountry || usernameInput.value) {
      usernameInput.dataset.postnumberBody = body;
    }

    const nextCountry = countrySelect.value.trim().toLocaleLowerCase();
    usernameInput.dataset.countryPrefix = nextCountry;
    usernameInput.disabled = !nextCountry;
    usernameInput.value = nextCountry ? body : "";
    if (countryPreviewInput) {
      countryPreviewInput.value = nextCountry.toLocaleUpperCase();
      countryPreviewInput.disabled = true;
    }
    currentCountry = nextCountry;
    usernameInput.dispatchEvent(new Event("country-prefix-sync"));

    return body;
  };

  const syncPreview = () => {
    const bodySource =
      syncUsernameInput() ||
      previewInputs[0]?.dataset.localpartPreviewBody ||
      previewElements[0]?.dataset.localpartPreviewBody ||
      "username";
    const body = bodySource.toLocaleLowerCase() || "username";
    const country = countrySelect.value.trim().toLocaleLowerCase() || "cc";

    for (const previewElement of previewElements) {
      const serverName = previewElement.dataset.serverName ?? "";
      previewElement.textContent = `@${country}${body}:${serverName}`;
    }

    for (const previewInput of previewInputs) {
      const serverName = previewInput.dataset.serverName ?? "";
      previewInput.value = `@${country}${body}:${serverName}`;
    }

    if (generateButton) {
      generateButton.disabled =
        !countrySelect.value || generateButton.dataset.loading === "true";
    }
  };

  if (usernameInput && hasCountryPrefixedInput) {
    formElement.addEventListener("formdata", (event) => {
      event.formData.set(
        usernameInput.name,
        extractPostnumberBodyValue(usernameInput),
      );
    });
  }

  syncPreview();
  countrySelect.addEventListener("change", syncPreview);
  usernameInput?.addEventListener("input", syncPreview);
  usernameInput?.addEventListener("blur", syncPreview);

  generateButton?.addEventListener("click", async () => {
    const url = generateButton.dataset.postnumberGenerationUrl;
    const csrfToken =
      formElement.querySelector<HTMLInputElement>('input[name="csrf"]')?.value;
    const country = countrySelect.value.trim().toLocaleLowerCase();

    if (!url || !csrfToken || !usernameInput) return;

    if (!country) {
      countrySelect.reportValidity();
      return;
    }

    generateButton.dataset.loading = "true";
    generateButton.disabled = true;
    generateButton.setAttribute("aria-busy", "true");
    generateButton.setAttribute("aria-label", "Generating PostNumber");

    try {
      const body = new URLSearchParams({
        csrf: csrfToken,
        country_code: country,
      });
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body,
      });

      if (!response.ok) {
        throw new Error(`Generation failed with status ${response.status}`);
      }

      const generated = (await response.json()) as {
        country_code: string;
        body: string;
        postnumber: string;
      };

      countrySelect.value = generated.country_code;
      currentCountry = generated.country_code;
      usernameInput.dataset.countryPrefix = generated.country_code;
      usernameInput.dataset.postnumberBody = generated.body;
      usernameInput.value = generated.body;
      usernameInput.setCustomValidity("");
      usernameInput.dispatchEvent(new Event("country-prefix-sync"));
      syncPreview();
      usernameInput.focus();
    } catch (error) {
      console.warn("Could not generate PostNumber", error);
      usernameInput.setCustomValidity("Could not generate a PostNumber");
      usernameInput.reportValidity();
    } finally {
      delete generateButton.dataset.loading;
      generateButton.removeAttribute("aria-busy");
      generateButton.setAttribute("aria-label", generateButtonLabel);
      syncPreview();
    }
  });
}

for (const formElement of document.querySelectorAll<HTMLFormElement>("form")) {
  patchCountryAwareForm(formElement);
}

// Look for username inputs on the page and patch them
for (const element of document.querySelectorAll<HTMLInputElement>(
  "input[data-choose-username]",
)) {
  patchUsernameInput(element);
}

function patchDisableSubmitUntilValid(formElement: HTMLFormElement) {
  const submitButton = formElement.querySelector<HTMLButtonElement>(
    '.cpd-button[data-kind="primary"][type="submit"]',
  );

  if (!submitButton) return;

  const syncDisabledState = () => {
    submitButton.disabled = !formElement.checkValidity();
  };

  syncDisabledState();

  formElement.addEventListener("input", syncDisabledState);
  formElement.addEventListener("change", syncDisabledState);
}

for (const formElement of document.querySelectorAll<HTMLFormElement>(
  "form[data-disable-submit-until-valid]",
)) {
  patchDisableSubmitUntilValid(formElement);
}
